import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { message, Select, InputNumber, Spin, Table, Button, Tag, Checkbox } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatCurrency, toApiNumber, roundToIqdDenomination } from "../../../utils/formatters";
import { apiClient } from "../../../shared/api/AllApi";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates, EXCHANGE_RATES_UPDATED_EVENT } from "../../../shared/lib/exchangeRates";
import { FormPage, FormCard, T, labelStyle } from "../../../shared/ui/PageShell";
import { orderDocNo } from "../../../shared/lib/documentNumbers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  useCustomersCatalog,
  useProductsCatalog,
} from "../../../hooks/useCatalogData";

dayjs.extend(utc);

const lineSubtotal = (item) => {
  const price = toApiNumber(item.single_price, 0);
  const w = toApiNumber(item.weight, 0);
  const q = toApiNumber(item.quantity, 0);
  const billing = w > 0 ? w : q;
  return price * billing;
};

const EditOrder = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [amountPaid, setAmountPaid] = useState(0);
  const [amountPaidMain, setAmountPaidMain] = useState(0);
  const [amountPaidSecondary, setAmountPaidSecondary] = useState(0);
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const [exchangeRates, setExchangeRates] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [allowAdjustments, setAllowAdjustments] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [newProductId, setNewProductId] = useState(null);
  const [documentNo, setDocumentNo] = useState(null);
  const [isTaxed, setIsTaxed] = useState(true);

  const { data: customerOptions } = useCustomersCatalog();
  const { data: storeProducts } = useProductsCatalog();

  const currency = systemConfig?.currency_code || "IQD";
  const useDualCurrency = Boolean(systemConfig?.secondary_currency_code);

  const getTranslatedName = useCallback(
    (nameInput) => {
      let names;
      try {
        names = typeof nameInput === "string" ? JSON.parse(nameInput) : nameInput;
      } catch {
        return typeof nameInput === "string" ? nameInput : t("unnamedProduct", "Unnamed");
      }
      return names?.[i18n.language] || names?.en || t("unnamedProduct", "N/A");
    },
    [i18n.language, t]
  );

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedMainTotal = useMemo(() => {
    const main = toApiNumber(amountPaidMain, 0);
    const sec = toApiNumber(amountPaidSecondary, 0);
    if (!useDualCurrency) return toApiNumber(amountPaid, 0);
    const secondaryPerMain = selectedRate ? toApiNumber(selectedRate.main_per_secondary, 0) : 0;
    if (!secondaryPerMain) return Math.round(main * 100) / 100;
    return Math.round((main + sec / secondaryPerMain) * 100) / 100;
  }, [amountPaid, amountPaidMain, amountPaidSecondary, selectedRate, useDualCurrency]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await apiClient.get(`/orders/${id}`);
        if (cancelled) return;
        const res = { ...response.data };
        const toNumber = (v) => {
          if (v === null || v === undefined) return 0;
          if (typeof v === "number") return v;
          try {
            return parseFloat(String(v).replace(/,/g, "")) || 0;
          } catch {
            return 0;
          }
        };

        setCart(
          (res.order_products || []).map((item) => ({
            product_id: item.product_id,
            quantity: toNumber(item.quantity),
            weight: item.weight ? toNumber(item.weight) : null,
            single_price: toNumber(item.single_price),
            sub_qty: toNumber(item.sub_qty) || null,
            sub_price: toNumber(item.sub_price) || null,
            product: item.product,
          }))
        );
        setCustomer(res.customer?.id || null);
        setDocumentNo(res.document_no || orderDocNo(res));
        setIsTaxed(!(res.is_taxed === false || res.is_taxed === 0));
        setAmountPaid(toNumber(res.amount_paid) || 0);
        if (res.amount_paid_main != null || res.amount_paid_secondary != null) {
          setAmountPaidMain(toApiNumber(res.amount_paid_main, toNumber(res.amount_paid) || 0));
          setAmountPaidSecondary(toApiNumber(res.amount_paid_secondary, 0));
        } else {
          setAmountPaidMain(toApiNumber(res.amount_paid, 0));
          setAmountPaidSecondary(0);
        }
        setPaymentMethod(res.payment_method || "cash");

        const cfg = await fetchSystemConfig();
        if (cancelled) return;
        setSystemConfig(cfg);
        if (cfg?.secondary_currency_code) {
          const rates = await fetchExchangeRates();
          if (cancelled) return;
          setExchangeRates(rates);
          const preferred = res.exchange_rate_id;
          const rid =
            preferred && rates.some((r) => Number(r.id) === Number(preferred))
              ? Number(preferred)
              : rates[0]?.id ?? null;
          setExchangeRateId(rid);
        } else {
          setExchangeRates([]);
          setExchangeRateId(null);
        }

        setPaymentsLoading(true);
        try {
          const pr = await apiClient.get(`/orders/${id}/payments`);
          if (!cancelled) setPayments(pr.data || []);
        } catch {
          if (!cancelled) setPayments([]);
        } finally {
          if (!cancelled) setPaymentsLoading(false);
        }
      } catch {
        message.error(t("failedLoadOrder", "Failed to load order for editing"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, t]);

  useEffect(() => {
    const onFxUpdated = () => {
      (async () => {
        try {
          const cfg = await fetchSystemConfig();
          setSystemConfig(cfg);
          if (!cfg?.secondary_currency_code) {
            setExchangeRates([]);
            setExchangeRateId(null);
            return;
          }
          const rates = await fetchExchangeRates();
          setExchangeRates(rates);
          setExchangeRateId((prev) =>
            prev && rates.some((r) => Number(r.id) === Number(prev)) ? prev : rates[0]?.id ?? null
          );
        } catch { /* ignore */ }
      })();
    };
    window.addEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
    return () => window.removeEventListener(EXCHANGE_RATES_UPDATED_EVENT, onFxUpdated);
  }, []);

  const updateLine = (index, field, value) => {
    setCart((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const removeLine = (index) => setCart((prev) => prev.filter((_, i) => i !== index));

  const addProduct = () => {
    if (!newProductId) return message.error(t("selectProductToAdd", "Please select a product to add"));
    if (cart.some((c) => c.product_id === newProductId)) {
      setNewProductId(null);
      return message.warning(t("productAlreadyInCart", "Product already in the cart"));
    }
    const product = storeProducts.find((p) => p.id === newProductId);
    if (!product) return message.error(t("productNotFound", "Selected product not found"));
    setCart((prev) => [
      ...prev,
      {
        product_id: product.id,
        quantity: 1,
        weight: null,
        single_price: parseFloat(product.price || 0),
        sub_qty: null,
        sub_price: null,
        product,
      },
    ]);
    setNewProductId(null);
  };

  const productSubtotal = useMemo(
    () => Math.round(cart.reduce((s, item) => s + lineSubtotal(item), 0) * 100) / 100,
    [cart]
  );
  const productQty = useMemo(
    () => cart.reduce((s, item) => s + Math.max(0, toApiNumber(item.quantity, 0)), 0),
    [cart]
  );
  const commissionPercent = parseFloat(systemConfig?.pos_commission_percent || 0) || 0;
  const commissionMode = systemConfig?.pos_commission_mode || "percent";
  const perQtyAmount = parseFloat(systemConfig?.pos_commission_per_qty_amount || 0);
  const commissionAmount = useMemo(() => {
    if (!isTaxed) return 0;
    const pctPart = (commissionMode === "percent" || commissionMode === "mix") && commissionPercent > 0
      ? (productSubtotal * commissionPercent) / 100
      : 0;
    const perQtyPart = commissionMode === "per_qty" || commissionMode === "mix"
      ? productQty * (perQtyAmount || 0)
      : 0;
    return pctPart + perQtyPart;
  }, [productSubtotal, productQty, commissionPercent, commissionMode, perQtyAmount, isTaxed]);
  // Only the final total uses cash-denomination rounding from system settings.
  const cartTotal = roundToIqdDenomination(productSubtotal + commissionAmount);
  const outstanding = Math.max(0, Math.round((cartTotal - appliedMainTotal) * 100) / 100);

  const columns = [
    {
      title: <b>{t("product", "Product")}</b>,
      key: "product",
      render: (_, item) => (
        <span style={{ fontWeight: 600, color: T.text }}>
          {getTranslatedName(item.product?.name) || `#${item.product_id}`}
        </span>
      ),
    },
    {
      title: <b>{t("quantity", "Qty")}</b>,
      key: "quantity",
      width: 120,
      render: (_, item, index) => (
        <InputNumber
          min={0}
          value={item.quantity}
          onChange={(v) => updateLine(index, "quantity", v === null ? 0 : Number(v))}
          controls={false}
          className="edit-order-input"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: <b>{t("weight", "Weight")} ({t("kg", "kg")})</b>,
      key: "weight",
      width: 130,
      render: (_, item, index) => (
        <InputNumber
          min={0}
          value={item.weight}
          placeholder={t("kg", "kg")}
          onChange={(v) => updateLine(index, "weight", v === null || v === "" ? null : Math.max(0, Number(v)))}
          controls={false}
          className="edit-order-input"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: <b>{t("price", "Price")}</b>,
      key: "single_price",
      width: 140,
      render: (_, item, index) => (
        <InputNumber
          min={0}
          value={item.single_price}
          onChange={(v) => updateLine(index, "single_price", v === null ? 0 : Math.max(0, Number(v)))}
          controls={false}
          className="edit-order-input"
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: <b>{t("subtotal", "Subtotal")}</b>,
      key: "subtotal",
      width: 150,
      render: (_, item) => (
        <span style={{ fontWeight: 700, color: T.primary }}>{formatCurrency(lineSubtotal(item), currency)}</span>
      ),
    },
    {
      title: "",
      key: "remove",
      width: 56,
      render: (_, __, index) => (
        <Button
          danger
          type="text"
          icon={<DeleteOutlined />}
          onClick={() => removeLine(index)}
        />
      ),
    },
  ];

  const handleSubmit = async () => {
    // Guard against double-submit: two Save buttons (header + footer) can both
    // fire before React re-renders with saving=true, sending the same edit
    // twice and applying the paid/debt swing 2x on the backend. A ref is used
    // instead of the `saving` state because it updates synchronously, closing
    // the race window that a state update (batched, async) would leave open.
    if (submittingRef.current) return;
    if (!customer) { message.error(t("pleaseSelectCustomer", "Please select customer")); return; }
    if (cart.length === 0) { message.error(t("cartEmpty", "Add at least one product")); return; }

    const toMoney = (value) => Math.round(toApiNumber(value, 0) * 100) / 100;
    const dual = Boolean(systemConfig?.secondary_currency_code);
    const secVal = toMoney(amountPaidSecondary);
    if (dual && secVal > 0 && !exchangeRateId) {
      message.error(t("pleaseSelectExchangeRate", "Please select an exchange rate."));
      return;
    }
    if (payments.length > 0 && !allowAdjustments) {
      message.error(
        t("orderHasPaymentsAdjust", "This order has payments. Enable adjustments to proceed or contact accounting.")
      );
      return;
    }

    const orderJson = {
      customer_id: customer,
      payment_method: paymentMethod,
      order_items: cart.map((item) => ({
        product_id: item.product_id,
        quantity: toApiNumber(item.quantity, 0),
        weight: item.weight > 0 ? toApiNumber(item.weight, 0) : null,
        single_price: toApiNumber(item.single_price, 0),
        sub_qty: item.sub_qty,
        sub_price: item.sub_price,
        subtotal: lineSubtotal(item),
      })),
    };
    if (dual) {
      orderJson.amount_paid_main = toMoney(amountPaidMain);
      orderJson.amount_paid_secondary = secVal;
      if (secVal > 0) orderJson.exchange_rate_id = exchangeRateId;
    } else {
      orderJson.amount_paid = toMoney(amountPaid);
    }
    if (payments.length > 0 && allowAdjustments) orderJson.create_adjustment = true;

    submittingRef.current = true;
    setSaving(true);
    try {
      await apiClient.put(`/orders/${id}`, orderJson);
      message.success(t("orderUpdated", "Order updated successfully"));
      navigate(`/orders/${id}/view`);
    } catch (error) {
      message.error(error?.response?.data?.message || t("failedUpdateOrder", "Failed to update order"));
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spin tip={t("loadingOrder", "Loading order...")} />
      </div>
    );
  }

  const availableProducts = storeProducts.filter((p) => !cart.some((c) => c.product_id === p.id));

  return (
    <FormPage
      breadcrumb={["Sales", t("orders", "Orders"), t("edit", "Edit")]}
      title={`${t("editOrder", "Edit Order")} ${documentNo || orderDocNo({ id, created_at: null })}`}
      subtitle={t("editOrderSubtitle", "Adjust items, quantities and payment")}
      onBack={() => navigate(-1)}
      onSave={handleSubmit}
      saving={saving}
    >
      <div className="edit-order-page">
      {/* Items — cart-style */}
      <FormCard title={t("items", "Items")} subtitle={t("cartStyleEdit", "Set quantity or weight per line")}>
        <Table
          rowKey="product_id"
          dataSource={cart}
          columns={columns}
          pagination={false}
          size="middle"
          scroll={{ x: "max-content" }}
          locale={{ emptyText: t("cartEmpty", "No items — add a product below") }}
        />
        <div style={{ display: "flex", gap: 10, marginTop: 16, alignItems: "center", flexWrap: "wrap" }}>
          <Select
            showSearch
            value={newProductId}
            onChange={setNewProductId}
            style={{ minWidth: 280, flex: 1 }}
            placeholder={t("selectProductToAdd", "Select a product to add")}
            optionFilterProp="label"
            options={availableProducts.map((p) => ({
              value: p.id,
              label: `${getTranslatedName(p.name)} — ${formatCurrency(p.price || 0, currency)}`,
            }))}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={addProduct} style={{ height: 42, borderRadius: 9 }}>
            {t("addProduct", "Add Product")}
          </Button>
        </div>

        <div
          style={{
            display: "flex", justifyContent: "flex-end", alignItems: "flex-end", gap: 24, flexWrap: "wrap",
            marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.borderL}`,
          }}
        >
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 12, color: T.muted }}>{t("vendorGross", "Products")}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.sub }}>{formatCurrency(productSubtotal, currency)}</div>
          </div>
          {isTaxed && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: T.muted }}>
                {t("posTerminalCommission", "Commission")}
                {commissionPercent > 0 ? ` (${commissionPercent}%)` : ""}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#7c3aed" }}>
                +{formatCurrency(commissionAmount, currency)}
              </div>
            </div>
          )}
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 12, color: T.muted }}>{t("cartSubtotal", "Cart Subtotal")}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: T.text }}>{formatCurrency(cartTotal, currency)}</div>
          </div>
        </div>
      </FormCard>

      {/* Customer / payment */}
      <FormCard title={t("customerAndPayment", "Customer & Payment")}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>{t("selectCustomer", "Customer")} <span style={{ color: T.red }}>*</span></div>
            <Select
              showSearch
              placeholder={t("selectCustomer", "Select a customer")}
              value={customer}
              onChange={setCustomer}
              optionFilterProp="label"
              style={{ width: "100%", marginTop: 6 }}
              options={customerOptions.map((c) => ({ value: c.id, label: c.name }))}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={labelStyle}>{t("paymentMethod", "Payment Method")}</div>
            <Select
              value={paymentMethod}
              onChange={setPaymentMethod}
              style={{ width: "100%", marginTop: 6 }}
              options={[
                { value: "cash", label: t("cash", "Cash") },
                { value: "credit", label: t("credit", "Credit") },
              ]}
            />
          </div>

          {useDualCurrency ? (
            <>
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>{`${t("amountPaid", "Amount Paid")} (${currency})`}</div>
                <InputNumber
                  min={0}
                  value={amountPaidMain}
                  onChange={(v) => setAmountPaidMain(Number(v) || 0)}
                  controls={false}
                  className="edit-order-input"
                  style={{ width: "100%", marginTop: 6 }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={labelStyle}>{`${t("amountPaid", "Amount Paid")} (${systemConfig?.secondary_currency_code || ""})`}</div>
                <InputNumber
                  min={0}
                  value={amountPaidSecondary}
                  onChange={(v) => setAmountPaidSecondary(Number(v) || 0)}
                  controls={false}
                  className="edit-order-input"
                  style={{ width: "100%", marginTop: 6 }}
                />
              </div>
              {exchangeRates.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={labelStyle}>{t("exchangeRate", "Exchange rate")}</div>
                  <Select
                    placeholder={t("selectRate", "Select rate")}
                    value={exchangeRateId}
                    onChange={setExchangeRateId}
                    style={{ width: "100%", marginTop: 6 }}
                    options={exchangeRates.map((r) => ({
                      value: r.id,
                      label: `${String(r.effective_from || "").slice(0, 10)} — 1 ${currency} = ${r.main_per_secondary} ${systemConfig?.secondary_currency_code}`,
                    }))}
                  />
                </div>
              )}
            </>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>{t("amountPaid", "Amount Paid")}</div>
              <InputNumber
                min={0}
                value={amountPaid}
                onChange={(v) => setAmountPaid(Number(v) || 0)}
                controls={false}
                className="edit-order-input"
                style={{ width: "100%", marginTop: 6 }}
              />
            </div>
          )}
        </div>

        {/* Totals summary */}
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 8, paddingTop: 16, borderTop: `1px solid ${T.borderL}` }}>
          <SummaryItem label={t("vendorGross", "Products")} value={formatCurrency(productSubtotal, currency)} color={T.sub} />
          {isTaxed && (
            <SummaryItem
              label={t("posTerminalCommission", "Commission")}
              value={`+${formatCurrency(commissionAmount, currency)}`}
              color="#7c3aed"
            />
          )}
          <SummaryItem label={t("total", "Total")} value={formatCurrency(cartTotal, currency)} color={T.text} />
          <SummaryItem label={t("amountPaid", "Paid")} value={formatCurrency(appliedMainTotal, currency)} color={T.green} />
          <SummaryItem label={t("outstanding", "Outstanding")} value={formatCurrency(outstanding, currency)} color={outstanding > 0 ? T.red : T.muted} />
        </div>
      </FormCard>

      {/* Payments history / adjustments */}
      <FormCard title={t("payments", "Payments")}>
        {paymentsLoading ? (
          <Spin />
        ) : payments.length === 0 ? (
          <div style={{ color: T.muted, fontSize: 13 }}>{t("noPaymentsRecorded", "No payments recorded for this order.")}</div>
        ) : (
          <div>
            <div style={{ marginBottom: 10, fontSize: 13 }}>
              <b>{t("totalPaid", "Total Paid")}: </b>
              <Tag color="green">{formatCurrency(payments.reduce((s, p) => s + Number(p.amount || 0), 0), currency)}</Tag>
            </div>
            <div style={{ maxHeight: 180, overflow: "auto", border: `1px solid ${T.border}`, borderRadius: 8, padding: 10 }}>
              {payments.map((p, i) => (
                <div key={i} style={{ marginBottom: 8, fontSize: 13, borderBottom: `1px solid ${T.borderL}`, paddingBottom: 6 }}>
                  <div style={{ fontWeight: 600, color: T.text }}>{dayjs(p.created_at).format("DD/MM/YYYY HH:mm")}</div>
                  <div style={{ color: T.sub }}>
                    {t("amount", "Amount")}: {formatCurrency(p.amount, currency)} {p.note ? `— ${p.note}` : ""}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <Checkbox checked={allowAdjustments} onChange={(e) => setAllowAdjustments(e.target.checked)}>
                {t("allowAdjustments", "Allow adjustments to this order (will create adjustment records)")}
              </Checkbox>
            </div>
          </div>
        )}
      </FormCard>
      </div>
    </FormPage>
  );
};

function SummaryItem({ label, value, color }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ fontSize: 12, color: T.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 800, color }}>{value}</span>
    </div>
  );
}

export default EditOrder;
